import React, { useState, useMemo } from "react";
import { Button } from "../button/Button";
import { Icon } from "../icon/Icon";

export type CheckboxFilterOption = {
  value: string;
  label: string;
};

type Props = {
  options: CheckboxFilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  testIdBase?: string;
};

export const CheckboxFilter: React.FC<Props> = ({
  options,
  selected,
  onChange,
  placeholder = "Filter options...",
  disabled = false,
  testIdBase,
}) => {
  const [search, setSearch] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const searchLower = search.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchLower)
    );
  }, [options, search]);

  const handleToggle = (value: string): void => {
    if (disabled) return;
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleClear = (): void => {
    if (disabled) return;
    onChange([]);
    setSearch("");
  };

  const selectedCount = selected.length;
  const displayText =
    selectedCount === 0
      ? placeholder
      : selectedCount === 1
      ? options.find((o) => o.value === selected[0])?.label || selected[0]
      : `${selectedCount} selected`;

  return (
    <div className="tw-relative">
      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={(): void => setIsExpanded(!isExpanded)}
        className={`
          tw-w-full tw-flex tw-items-center tw-justify-between
          tw-px-3 tw-py-2
          tw-text-sm tw-text-gray-900
          tw-bg-white
          tw-border tw-border-gray-300 tw-rounded-md
          tw-shadow-sm
          tw-ring-1 tw-ring-inset tw-ring-gray-300
          focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-gray-400
          disabled:tw-bg-gray-50 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed
          hover:tw-ring-gray-400
          ${isExpanded ? "tw-ring-2 tw-ring-gray-400" : ""}
        `}
        data-testid={
          testIdBase ? `${testIdBase}-trigger` : "checkbox-filter-trigger"
        }
      >
        <span className="tw-truncate tw-flex-1 tw-text-left">
          {displayText}
        </span>
        <div className="tw-flex tw-items-center tw-gap-2 tw-ml-2">
          {selectedCount > 0 && (
            <span className="tw-text-xs tw-bg-gray-100 tw-text-gray-700 tw-px-2 tw-py-0.5 tw-rounded-full">
              {selectedCount}
            </span>
          )}
          <Icon
            name={isExpanded ? "expand_less" : "expand_more"}
            className="tw-text-gray-500"
          />
        </div>
      </button>

      {/* Dropdown Panel */}
      {isExpanded && (
        <div
          className="tw-absolute tw-z-50 tw-mt-1 tw-w-full
            tw-bg-white tw-border tw-border-gray-300 tw-rounded-md tw-shadow-lg
            tw-max-h-96 tw-flex tw-flex-col"
          data-testid={
            testIdBase ? `${testIdBase}-panel` : "checkbox-filter-panel"
          }
          onClick={(e): void => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="tw-p-2 tw-border-b tw-border-gray-200">
            <input
              type="text"
              value={search}
              onChange={(e): void => setSearch(e.target.value)}
              placeholder="Search..."
              className="tw-w-full tw-px-2 tw-py-1.5 tw-text-sm
                tw-border tw-border-gray-300 tw-rounded
                focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-border-transparent"
              data-testid={
                testIdBase ? `${testIdBase}-search` : "checkbox-filter-search"
              }
            />
          </div>

          {/* Options List */}
          <div className="tw-overflow-y-auto tw-flex-1">
            {filteredOptions.length === 0 ? (
              <div className="tw-p-3 tw-text-center tw-text-gray-400 tw-text-sm">
                No options found
              </div>
            ) : (
              <div className="tw-p-1">
                {filteredOptions.map((option) => {
                  const isChecked = selected.includes(option.value);
                  return (
                    <label
                      key={option.value}
                      className={`
                        tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-rounded
                        tw-cursor-pointer hover:tw-bg-gray-50
                        ${isChecked ? "tw-bg-gray-50" : ""}
                        ${disabled ? "tw-cursor-not-allowed tw-opacity-50" : ""}
                      `}
                      data-testid={
                        testIdBase
                          ? `${testIdBase}-option-${option.value}`
                          : `checkbox-filter-option-${option.value}`
                      }
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(): void => handleToggle(option.value)}
                        disabled={disabled}
                        className="tw-form-checkbox tw-h-4 tw-w-4 tw-rounded
                          tw-border-gray-400 tw-text-textual-blue
                          focus:tw-ring-gray-400
                          disabled:tw-border-gray-200 disabled:tw-text-gray-200"
                      />
                      <span className="tw-text-sm tw-text-gray-900 tw-select-none">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer with Clear Button */}
          {selectedCount > 0 && (
            <div className="tw-p-2 tw-border-t tw-border-gray-200">
              <Button
                variant="secondary-alt"
                size="sm"
                onClick={handleClear}
                disabled={disabled}
                className="tw-w-full"
                data-testid={
                  testIdBase ? `${testIdBase}-clear` : "checkbox-filter-clear"
                }
              >
                <Icon name="delete" />
                Clear selection
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Backdrop to close on click outside */}
      {isExpanded && (
        <div
          className="tw-fixed tw-inset-0 tw-z-40"
          onClick={(): void => setIsExpanded(false)}
          data-testid={
            testIdBase ? `${testIdBase}-backdrop` : "checkbox-filter-backdrop"
          }
        />
      )}
    </div>
  );
};
